import {
  cn,
  createDataTableColumnHelper,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Link as UiLink,
} from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { useRouteOverlayOpen } from "#/hooks/use-route-overlay-open";
import { LazySportdeelnameFieldCell } from "#/modules/sportdeelname/components/sportdeelname-field-cells";
import { formatVaardigheidCell } from "#/modules/sportdeelname/format-vaardigheid";
import type { SportdeelnameStatKpi } from "#/modules/sportdeelname/sportdeelname-stat-kpi";
import { getSportParticipationKpiLeerlingen } from "#/modules/sportdeelname/sportdeelname.functions";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/sport-participation/stats/$kpi",
)({
  // No search params of its own — `klasIds` is inherited from the parent `sport-participation`
  // route's `validateSearch`. Narrowed to exactly the key the loader uses.
  loaderDeps: ({ search }) => ({ klasIds: search.klasIds }),
  loader: ({ deps, params }) =>
    getSportParticipationKpiLeerlingen({
      data: {
        filters: deps,
        kpi: params.kpi,
        locale: params.locale,
        school: params.school,
        schoolyear: params.schoolyear,
      },
    }),
  component: SportParticipationStatDetailDialog,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

function kpiTitle(kpi: SportdeelnameStatKpi): string {
  switch (kpi) {
    case "geen-zwemdiploma":
      return m["sportdeelname.stats.kpi_no_swim_diploma"]();
    case "zonder-sport":
      return m["sportdeelname.stats.kpi_no_sport"]();
    case "motorische-zorg":
      return m["sportdeelname.stats.kpi_motor_care"]();
    case "zonder-sport-en-zonder-diploma":
      return m["sportdeelname.stats.kpi_no_sport_no_diploma"]();
    case "kan-niet-fietsen":
      return m["sportdeelname.stats.kpi_cannot_cycle"]();
  }
}

type KpiLeerlingRow = Awaited<
  ReturnType<typeof getSportParticipationKpiLeerlingen>
>["data"]["leerlingen"][number];

const helper = createDataTableColumnHelper<KpiLeerlingRow>();

const columns = helper.columns([
  helper.accessor("naam", {
    header: () => m["common.fields.student"](),
    meta: { rowTarget: true, cellClassName: "font-medium" },
    cell: ({ row, getValue }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
        params={(prev: Record<string, string>) => ({ ...prev, id: row.original.leerlingId })}
      >
        {getValue()}
      </UiLink>
    ),
  }),
  helper.accessor("klasNaam", {
    header: () => m["common.fields.klas"](),
    meta: { cellClassName: "text-muted-foreground text-sm" },
  }),
  helper.accessor("sporten", {
    header: () => m["sportdeelname.sporten"](),
    meta: { headerClassName: "min-w-[14ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="sporten"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={row.original.hasNoSport ? [m["sportdeelname.sport_niet"]()] : getValue()}
      />
    ),
  }),
  helper.accessor("verenigingen", {
    header: () => m["sportdeelname.verenigingen"](),
    meta: { headerClassName: "min-w-[14ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="verenigingen"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={getValue()}
      />
    ),
  }),
  helper.accessor("wensSporten", {
    header: () => m["sportdeelname.wenssporten"](),
    meta: { headerClassName: "min-w-[14ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="wenssporten"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={getValue()}
      />
    ),
  }),
  helper.accessor("mrt", {
    header: () => m["sportdeelname.mrt"](),
    meta: { headerClassName: "min-w-[10ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="mrt"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={getValue() ? [m["common.yes"]()] : []}
      />
    ),
  }),
  helper.accessor("zwemvaardigheid", {
    header: () => m["sportdeelname.zwemvaardigheid"](),
    meta: { headerClassName: "min-w-[14ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="zwemvaardigheid"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={formatVaardigheidCell(getValue())}
      />
    ),
  }),
  helper.accessor("fietsvaardigheid", {
    header: () => m["sportdeelname.fietsvaardigheid"](),
    meta: { headerClassName: "min-w-[14ch]" },
    cell: ({ row, getValue }) => (
      <LazySportdeelnameFieldCell
        field="fietsvaardigheid"
        leerlingId={row.original.leerlingId}
        leerlingNaam={row.original.naam}
        items={formatVaardigheidCell(getValue())}
      />
    ),
  }),
]);

function SportParticipationStatDetailDialog() {
  const { locale, schoolyear, school } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const dialog = useRouteOverlayOpen(() => {
    void navigate({
      to: "/$locale/{-$schoolyear}/$school/insights/sport-participation",
      params: { locale, schoolyear, school },
      search: (prev) => ({ ...prev }),
      replace: true,
      resetScroll: false,
      viewTransition: true,
    });
  });

  const title = kpiTitle(loaderData.kpi);

  return (
    <Dialog {...dialog}>
      <DialogContent
        className={cn({
          "md:max-w-lg!": loaderData.data.leerlingen.length === 0,
        })}
        showCloseButton
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {m["sportdeelname.stats.kpi_leerlingen_subset"]({
              count: loaderData.data.leerlingen.length,
              total: loaderData.data.totalLeerlingen,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="no-scrollbar -mx-6 max-h-[50vh] overflow-y-auto px-6">
          {loaderData.data.leerlingen.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {m["common.empty_state.students_in_category"]()}
            </p>
          ) : (
            <DataTable
              size="small"
              aria-label={m["common.aria.students_in_count"]()}
              className="border-0"
              columns={columns}
              data={loaderData.data.leerlingen}
              getRowId={(row) => row.leerlingId}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
